import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, X, ChefHat, ShoppingCart, MessageSquare, DollarSign, 
  Users, Settings, Calendar, Package, FileText, AlertTriangle,
  Star, CheckCircle, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

// Role-specific onboarding content
const ROLE_ONBOARDING = {
  super_admin: {
    title: 'Welcome, Super Admin!',
    subtitle: 'You have full system access',
    color: 'from-red-500 to-rose-600',
    icon: Shield,
    features: [
      { icon: ShoppingCart, title: 'Orders', desc: 'View and manage all orders, process refunds, update statuses' },
      { icon: ChefHat, title: 'Kitchen', desc: 'Monitor prep status and kitchen operations' },
      { icon: DollarSign, title: 'Finance', desc: 'Access all financial data, revenue, and payments' },
      { icon: Users, title: 'Users', desc: 'Manage staff accounts, roles, and invitations' },
      { icon: Settings, title: 'Settings', desc: 'Configure system settings and API keys' },
      { icon: FileText, title: 'Reports', desc: 'Generate and export all reports' },
    ],
    quickTips: [
      'Use the sidebar to navigate between sections',
      'Check the Dashboard daily for key metrics',
      'Review food safety cases promptly',
    ]
  },
  operations_manager: {
    title: 'Welcome, Operations Manager!',
    subtitle: 'You manage daily operations',
    color: 'from-purple-500 to-violet-600',
    icon: Package,
    features: [
      { icon: ShoppingCart, title: 'Orders', desc: 'View all orders and update statuses' },
      { icon: ChefHat, title: 'Kitchen', desc: 'Monitor prep sheets and kitchen workflow' },
      { icon: Calendar, title: 'Menu Schedule', desc: 'Manage weekly menus and scheduling' },
      { icon: Package, title: 'Stock', desc: 'Track inventory levels' },
    ],
    restrictions: [
      'Financial data is not visible to this role',
      'System settings are managed by Super Admin',
    ],
    quickTips: [
      'Start your day by checking pending orders',
      'Monitor Kitchen Prep for smooth operations',
      'Keep stock levels updated',
    ]
  },
  kitchen: {
    title: 'Welcome, Kitchen Staff!',
    subtitle: 'Your prep dashboard is ready',
    color: 'from-orange-500 to-amber-600',
    icon: ChefHat,
    features: [
      { icon: ChefHat, title: 'Kitchen Prep', desc: 'View daily prep sheets and quantities' },
    ],
    restrictions: [
      'This role focuses on food preparation only',
      'Order details and prices are not visible',
      'Customer information is private',
    ],
    quickTips: [
      'Check Kitchen Prep at the start of your shift',
      'Mark items as "Started" when you begin prep',
      'Mark items as "Completed" when ready',
    ]
  },
  customer_support: {
    title: 'Welcome, Customer Support!',
    subtitle: 'Help our customers succeed',
    color: 'from-blue-500 to-cyan-600',
    icon: MessageSquare,
    features: [
      { icon: MessageSquare, title: 'Messages', desc: 'Respond to customer inquiries' },
      { icon: AlertTriangle, title: 'Food Safety', desc: 'Handle food safety reports' },
      { icon: Star, title: 'Reviews', desc: 'Monitor and respond to reviews' },
    ],
    restrictions: [
      'Order values and financial data are not visible',
      'Kitchen operations are managed by other roles',
    ],
    quickTips: [
      'Respond to messages promptly',
      'Escalate food safety issues immediately',
      'Be professional and helpful in all interactions',
    ]
  },
  finance: {
    title: 'Welcome, Finance!',
    subtitle: 'Full financial visibility',
    color: 'from-green-500 to-emerald-600',
    icon: DollarSign,
    features: [
      { icon: ShoppingCart, title: 'Orders', desc: 'View all orders (read-only)' },
      { icon: DollarSign, title: 'Payments', desc: 'Access payment data and Paystack fees' },
      { icon: FileText, title: 'Reports', desc: 'Generate financial reports and exports' },
    ],
    restrictions: [
      'Menu management is handled by Operations',
      'Kitchen prep is managed by Kitchen staff',
      'Order status changes require Operations role',
    ],
    quickTips: [
      'Use Reports for detailed financial analysis',
      'Export data to Excel for reconciliation',
      'Review daily totals regularly',
    ]
  }
};

export default function RoleOnboarding({ isOpen, onClose, userRole }) {
  const [currentStep, setCurrentStep] = useState(0);
  const roleData = ROLE_ONBOARDING[userRole] || ROLE_ONBOARDING.operations_manager;
  const RoleIcon = roleData.icon;

  const steps = [
    { id: 'welcome', title: 'Welcome' },
    { id: 'features', title: 'Your Access' },
    { id: 'tips', title: 'Quick Tips' },
  ];

  const handleComplete = () => {
    // Store onboarding completion in localStorage
    localStorage.setItem(`boh_onboarding_${userRole}`, 'completed');
    onClose();
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden" aria-describedby={undefined}>
        {/* Visually hidden title for screen reader accessibility */}
        <DialogTitle className="sr-only">
          {roleData.title} — Onboarding Guide
        </DialogTitle>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {/* Step 0: Welcome */}
            {currentStep === 0 && (
              <div className="p-0">
                <div className={`bg-gradient-to-r ${roleData.color} p-8 text-white text-center`}>
                  <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <RoleIcon className="h-10 w-10 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">{roleData.title}</h2>
                  <p className="text-white/80">{roleData.subtitle}</p>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 text-center mb-6">
                    This quick guide will help you understand what you can do with your role.
                  </p>
                  <div className="flex justify-center gap-2 mb-6">
                    {steps.map((step, idx) => (
                      <div
                        key={step.id}
                        className={`h-2 w-12 rounded-full transition-colors ${
                          idx <= currentStep ? 'bg-primary' : 'bg-slate-200'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Features */}
            {currentStep === 1 && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  What You Can Access
                </h3>
                <div className="space-y-3 mb-6">
                  {roleData.features.map((feature, idx) => {
                    const FeatureIcon = feature.icon;
                    return (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="p-2 bg-white rounded-lg shadow-sm">
                          <FeatureIcon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{feature.title}</p>
                          <p className="text-sm text-slate-500">{feature.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {roleData.restrictions && (
                  <>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Note:</h4>
                    <div className="space-y-1 text-sm text-slate-500">
                      {roleData.restrictions.map((r, idx) => (
                        <p key={idx} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full" />
                          {r}
                        </p>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 2: Tips */}
            {currentStep === 2 && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Tips</h3>
                <div className="space-y-3 mb-6">
                  {roleData.quickTips.map((tip, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                      <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-sm font-medium text-amber-700">
                        {idx + 1}
                      </span>
                      <p className="text-slate-700">{tip}</p>
                    </div>
                  ))}
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                  <p className="text-green-800 text-center font-medium">
                    You&apos;re all set! Click below to start.
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="p-4 border-t bg-slate-50 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={currentStep === 0 ? onClose : prevStep}
            className="text-slate-600"
          >
            {currentStep === 0 ? 'Skip' : 'Back'}
          </Button>
          <div className="flex gap-1">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`h-2 w-2 rounded-full transition-colors ${
                  idx === currentStep ? 'bg-primary' : 'bg-slate-300'
                }`}
              />
            ))}
          </div>
          <Button onClick={nextStep} className="bg-primary hover:bg-primary-600">
            {currentStep === steps.length - 1 ? "Let's Go!" : 'Next'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hook to check if onboarding should be shown
export function useRoleOnboarding(userRole) {
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (userRole) {
      const completed = localStorage.getItem(`boh_onboarding_${userRole}`);
      if (!completed) {
        // Small delay to let the page render first
        const timer = setTimeout(() => setShowOnboarding(true), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [userRole]);

  return {
    showOnboarding,
    closeOnboarding: () => {
      if (userRole) {
        localStorage.setItem(`boh_onboarding_${userRole}`, 'completed');
      }
      setShowOnboarding(false);
    },
    resetOnboarding: () => {
      if (userRole) {
        localStorage.removeItem(`boh_onboarding_${userRole}`);
        setShowOnboarding(true);
      }
    }
  };
}
